import React, { useState } from 'react';
import { Tooltip } from 'antd';
import type { TooltipProps } from 'antd';
import './CopyTooltip.css';

/**
 * A name cut to the space it has, in full in its tooltip — with a link after
 * it to copy it, since a name that does not fit on the page is the one that
 * is hard to select from it. `label` says what kind of name it is, for a name
 * whose icon says so on the page.
 *
 * The tooltip stays open while the cursor moves into it, so the link is there
 * to click; it says "copied" once it has, until the tooltip closes. With no
 * text there is nothing to copy: the tooltip says so, and offers no link.
 */
const CopyTooltip = ({
    text,
    label,
    placement,
    children,
}: {
    text?: string | null;
    label?: string;
    placement?: TooltipProps['placement'];
    children: React.ReactElement;
}) => {
    const [copied, setCopied] = useState(false);

    const copy = (event: React.MouseEvent) => {
        // the tooltip's link, not whatever the name underneath it opens
        event.stopPropagation();
        if (!text) return;
        navigator.clipboard?.writeText(text).then(() => setCopied(true), () => setCopied(false));
    };

    const title = (
        <span className="nx-copy-tip">
            {label && <span className="nx-copy-tip__label">{label}: </span>}
            <span className="nx-copy-tip__text">{text || '-'}</span>
            {text && (
                <button type="button" className="nx-copy-tip__hint" onClick={copy}>
                    {copied ? 'copied' : 'click to copy'}
                </button>
            )}
        </span>
    );

    return (
        <Tooltip
            title={title}
            placement={placement}
            rootClassName="nx-copy-tip-root"
            onOpenChange={(open) => { if (!open) setCopied(false); }}
        >
            {children}
        </Tooltip>
    );
};

export default CopyTooltip;
